import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React, {useState} from 'react';

import {overridePurpose} from 'archive/ArchiveActions';
import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import useAreOrganizationSchemasCreatableQuery from 'auth/useAreOrganizationSchemasCreatableQuery';
import * as BenchlingUtil from 'BenchlingUtil';
import {
  ProcessExecutionType,
  ProcessClientOrigin,
  ProcessPlatformFeatureType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery, {QueryResultType} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {getEnabledProcessExecutionTypesForPlatformFeature} from 'processes/ProcessConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import {ProcessTaskSchemasPageQuery} from 'processes/settings/__generated/ProcessTaskSchemasPageQuery';
import ProcessTaskSchemaExecutionTypeDropdown from 'processes/tables/ProcessTaskSchemaExecutionTypeDropdown';
import {canConfigureProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import SchemasListing, {SchemasListingSchema} from 'schemas/SchemasListing';
import {SCHEMA_TYPES} from 'schemas/spec/SchemaConfigConstants';
import {withLoadingScreen} from 'util/Promises';

interface ProcessTaskSchemasListingCommonProps {
  areOrganizationSchemasCreatable: boolean;
}

interface ProcessTaskSchemasListingPropsWithoutSchema extends ProcessTaskSchemasListingCommonProps {
  processTaskSchemas: null;
}

interface ProcessTaskSchemasListingSchema extends SchemasListingSchema {
  executionType: ProcessExecutionType;
  clientOrigin: ProcessClientOrigin;
}

interface ProcessTaskSchemasListingPropsWithSchema extends ProcessTaskSchemasListingCommonProps {
  processTaskSchemas: Array<ProcessTaskSchemasListingSchema>;
  refetch: () => void;
}

type ProcessTaskSchemasListingProps =
  | ProcessTaskSchemasListingPropsWithoutSchema
  | ProcessTaskSchemasListingPropsWithSchema;

export function ProcessTaskSchemasListing(props: ProcessTaskSchemasListingProps): JSX.Element {
  const [selectedExecutionTypes, setSelectedExecutionTypes] = useState(
    /* eslint-disable-next-line no-restricted-syntax */
    getEnabledProcessExecutionTypesForPlatformFeature(ProcessPlatformFeatureType.SCHEMA_CONFIG)
      .sort()
      .map((executionType) => executionType)
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleNavigateToCreate = () => {
    BenchlingUtil.navigate(null, ProcessSchemaDAO.createProcessTaskSchemaURL());
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const bulkArchiveProcessSchemas = async (ids: Array<string>, purpose: string) => {
    try {
      await withLoadingScreen(
        ProcessSchemaDAO.bulkArchive(ids, overridePurpose(purpose, ids)),
        'Archiving...'
      );
      notifications.success('Items successfully archived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    (props as ProcessTaskSchemasListingPropsWithSchema).refetch();
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const bulkUnarchiveProcessSchemas = async (ids: Array<string>) => {
    try {
      await ProcessSchemaDAO.bulkUnarchive(ids);
      notifications.success('Items successfully unarchived.');
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    }
    (props as ProcessTaskSchemasListingPropsWithSchema).refetch();
  };

  const handleChangeExecutionTypeFilters = (selectedExecutionTypes: Array<ProcessExecutionType>): void => {
    setSelectedExecutionTypes(selectedExecutionTypes);
  };

  const filterForExecutionTypes = (
    schemas: Array<ProcessTaskSchemasListingSchema>
  ): Array<ProcessTaskSchemasListingSchema> =>
    schemas.filter((item) => selectedExecutionTypes.includes(item.executionType));

  BenchlingUtil.setWindowTitle('Workflow Schemas');
  return (
    <SchemasListing
      schemas={props.processTaskSchemas}
      schemaType={SCHEMA_TYPES.PROCESS}
      columnHeaders={[
        {name: 'Schemas', width: 8},
        {name: 'Execution Type', width: 4},
      ]}
      customSearchControls={
        <ProcessTaskSchemaExecutionTypeDropdown
          onChangeExecutionTypes={handleChangeExecutionTypeFilters}
          selectedExecutionTypes={selectedExecutionTypes}
        />
      }
      customFilterCallback={filterForExecutionTypes}
      navigateToCreate={handleNavigateToCreate}
      urlForSchema={(schemaId: string) => ProcessSchemaDAO.processTaskSchemaURL(schemaId)}
      archiveSchemas={bulkArchiveProcessSchemas}
      unarchiveSchemas={bulkUnarchiveProcessSchemas}
      isWritable={props.areOrganizationSchemasCreatable}
      mapSchemaToRowValue={(schema) => [
        <span className="u-small-spacing-left">
          {schema.name}
          <ArchiveRecordLabel item={schema} />
        </span>,
        processExecutionTypeDisplayName(schema.clientOrigin, schema.executionType),
      ]}
    />
  );
}

export const PROCESS_TASK_SCHEMAS_PAGE_QUERY: DocumentNode = gql`
  query ProcessTaskSchemasPageQuery($platformFeature: ProcessPlatformFeatureType!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: $platformFeature) {
      id
      name
      archiveRecord {
        purpose
      }
      executionType
      clientOrigin
    }
  }
`;

export default function ProcessTaskSchemasPage(): JSX.Element | null {
  const areOrganizationSchemasCreatableResults = useAreOrganizationSchemasCreatableQuery();
  const areOrganizationSchemasCreatable =
    areOrganizationSchemasCreatableResults.queryResultType === QueryResultType.SUCCESS &&
    areOrganizationSchemasCreatableResults.areOrganizationSchemasCreatable;
  return (
    <BenchlingQuery<ProcessTaskSchemasPageQuery>
      query={PROCESS_TASK_SCHEMAS_PAGE_QUERY}
      variables={{
        platformFeature: ProcessPlatformFeatureType.SCHEMA_CONFIG,
      }}
      renderLoading={() => (
        <ProcessTaskSchemasListing
          processTaskSchemas={null}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
        />
      )}
      fetchPolicy="cache-and-network"
      renderCachedDataWhenRefetching={true}
    >
      {(data, {refetch}) => (
        <ProcessTaskSchemasListing
          processTaskSchemas={data.processTaskSchemasSupportingPlatformFeature.filter((schema) =>
            canConfigureProcessTaskSchema(schema)
          )}
          areOrganizationSchemasCreatable={areOrganizationSchemasCreatable}
          refetch={async () => {
            await refetch();
          }}
        />
      )}
    </BenchlingQuery>
  );
}
